const FRIENDLY = {
  FORBIDDEN: "You don't have permission for this action.",
  AUTH_REQUIRED: "Please sign in again.",
  SESSION_INVALID: "Your session has expired. Please sign in again.",
  DATABASE_ERROR: "The server database is unavailable right now. Please try again shortly.",
  DATA_CONFLICT: "This conflicts with existing data (duplicate or still in use).",
  NOT_IMPLEMENTED: "This feature needs a backend endpoint that is not available yet.",
};

function fieldName(field = "") {
  return field.replace(/^body\./, "").replace(/^data\./, "").replace(/_/g, " ");
}

export function getErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const body = error?.response?.data;
  const apiError = body?.error;
  if (apiError) {
    if (apiError.code === "VALIDATION_ERROR" && Array.isArray(apiError.details) && apiError.details.length) {
      const fields = [...new Set(apiError.details.map((d) => fieldName(d.field)).filter(Boolean))];
      return `Please check: ${fields.slice(0, 4).join(", ")}.`;
    }
    const message = apiError.message && apiError.message !== "Permission required" ? apiError.message : FRIENDLY[apiError.code];
    return message || FRIENDLY[apiError.code] || fallback;
  }
  const detail = body?.detail || body?.message;
  if (typeof detail === "string" && detail) return detail;
  if (error?.code === "NOT_IMPLEMENTED") return error.message || FRIENDLY.NOT_IMPLEMENTED;
  if (error?.request && !error?.response) {
    return "Unable to reach the server. Check that the backend is running and try again.";
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

// [NEW] Manager KRA/KPI workflow: clear messages for the four backend responses the managers will meet.
const TABLE_NAMES = {
  kra_master: "KRA", kpi_master: "KPI", designation: "Designation", performance_template: "Template",
  performance_record: "Appraisal record", performance_item: "Assigned item", employee: "Employee",
};
const STATUS_TEXT = {
  403: {
    title: "Not allowed",
    hint: "You can change only your own team definitions and assign only to your direct reports.",
  },
  404: {
    title: "Not found",
    hint: "The KRA, KPI, designation or template no longer exists. Reload the page and try again.",
  },
  409: {
    title: "Conflict",
    hint: "It is already assigned, or another mapping or a newer change conflicts with it.",
  },
  422: {
    title: "Please check the details",
    hint: "KRA weights must total 100%, every KRA needs at least one KPI (its KPIs share the KRA equally), and every KPI needs its parent KRA.",
  },
};

export function describeError(error) {
  const status = error?.response?.status || null;
  let message = getErrorMessage(error);
  const table = /^(\w+) not found$/.exec(message || "");
  if (table) message = `${TABLE_NAMES[table[1]] || table[1].replace(/_/g, " ")} not found.`;
  const known = STATUS_TEXT[status];
  return {
    status,
    title: known?.title || "Something went wrong",
    message,
    hint: known?.hint || "",
    text: known ? `${known.title} (${status}): ${message}${/[.!?]$/.test(message) ? "" : "."} ${known.hint}` : message,
  };
}

// Toast-ready text: "Not allowed (403): <backend message>. <what it means>".
export const workflowErrorText = (error) => describeError(error).text;

export function getErrorCode(error) {
  return error?.response?.data?.error?.code || error?.code || null;
}

export function isForbidden(error) {
  return error?.response?.status === 403;
}

export function isNotFound(error) {
  return error?.response?.status === 404 || error?.response?.status === 405;
}

export function notImplemented(message) {
  const err = new Error(message || FRIENDLY.NOT_IMPLEMENTED);
  err.code = "NOT_IMPLEMENTED";
  return err;
}
